import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SecuritySection = ({ className = '' }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [keyRotationStatus, setKeyRotationStatus] = useState('healthy');

  const securityMetrics = {
    keyPairHealth: 'healthy',
    lastKeyRotation: '2025-08-15T10:30:00Z',
    encryptionStrength: 'AES-256',
    backupStatus: 'synced',
    recoveryPhraseStatus: 'secured'
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': case'synced': case'secured':
        return 'text-success';
      case 'warning':
        return 'text-warning';
      case 'error':
        return 'text-error';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy': case'synced': case'secured':
        return 'ShieldCheck';
      case 'warning':
        return 'ShieldAlert';
      case 'error':
        return 'ShieldX';
      default:
        return 'Shield';
    }
  };

  const handleKeyRotation = () => {
    setKeyRotationStatus('rotating');
    // Simulate key rotation process
    setTimeout(() => {
      setKeyRotationStatus('healthy');
    }, 3000);
  };

  return (
    <div className={`credential-card ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Shield" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Wallet Security</h3>
            <p className="text-sm text-muted-foreground">Cryptographic key management</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={20} />
        </Button>
      </div>
      {/* Security Overview */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="flex items-center space-x-2">
          <Icon 
            name={getStatusIcon(securityMetrics?.keyPairHealth)} 
            size={16} 
            className={getStatusColor(securityMetrics?.keyPairHealth)} 
          />
          <span className="text-sm text-foreground">Key Pair Health</span>
        </div>
        <div className="flex items-center space-x-2">
          <Icon 
            name={getStatusIcon(securityMetrics?.backupStatus)} 
            size={16} 
            className={getStatusColor(securityMetrics?.backupStatus)} 
          />
          <span className="text-sm text-foreground">Backup Status</span>
        </div>
      </div>
      {/* Expanded Security Details */}
      {isExpanded && (
        <div className="space-y-4 pt-4 border-t border-border">
          {/* Key Information */}
          <div>
            <h4 className="font-medium text-foreground mb-3">Cryptographic Keys</h4>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Encryption:</span>
                <span className="text-sm text-foreground font-mono">{securityMetrics?.encryptionStrength}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Last Rotation:</span>
                <span className="text-sm text-foreground">{formatDate(securityMetrics?.lastKeyRotation)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Recovery Phrase:</span>
                <div className="flex items-center space-x-1">
                  <Icon name="Lock" size={14} className="text-success" />
                  <span className="text-sm text-success">Secured</span>
                </div>
              </div>
            </div>
          </div>

          {/* Key Actions */}
          <div>
            <h4 className="font-medium text-foreground mb-3">Security Actions</h4>
            <div className="space-y-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleKeyRotation}
                disabled={keyRotationStatus === 'rotating'}
                iconName={keyRotationStatus === 'rotating' ? "Loader2" : "RotateCcw"}
                iconPosition="left"
                fullWidth
              >
                {keyRotationStatus === 'rotating' ? 'Rotating Keys...' : 'Rotate Key Pair'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Download"
                iconPosition="left"
                fullWidth
              >
                Export Public Key
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Eye"
                iconPosition="left"
                fullWidth
              >
                View Recovery Phrase
              </Button>
            </div>
          </div>

          {/* Security Recommendations */}
          <div className="p-3 bg-accent/10 rounded-lg">
            <div className="flex items-start space-x-2">
              <Icon name="Info" size={16} className="text-accent mt-0.5" />
              <div>
                <p className="text-sm font-medium text-accent">Security Recommendation</p>
                <p className="text-xs text-accent/80 mt-1">
                  Consider rotating your key pair every 90 days for enhanced security.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecuritySection;